import React, { useEffect } from "react";
import {
  FormDataEditDetails,
  KrookieStatus,
} from "../../../../../interfaces/krookie";
import {
  KrookieFormationTypeId,
  setFormDataEditDetails,
} from "../../../../../services/reducers/master-plan-modification";
import { useAppSelector } from "../../../../../hooks/useAppSelector";
import { useAppDispatch } from "../../../../../hooks/useAppDispatch";
import { formatDateWithoutTime } from "../../../../../utils/formatDate";
import { ConvertToLines } from "../../../../../utils/master-plan-helper";
import { getNewChangeNumber } from "../../../../../utils/krookie-helpers";
import DetailsEditing from "../details-editing";
import { getPreviousKrookieNum } from "../../../../../services/api";

const DetailsMerger = () => {
  const dispatch = useAppDispatch();
  const {
    formDataEditDetails,
    krookiePlots,
    cities,
    screenshots,
    phase,
    landUse,
    selectedPlots,
    convertedKrookieData,
  } = useAppSelector((state) => state.masterPlanModification);
  const { user } = useAppSelector((state) => state.user);

  const currentPlot = krookiePlots[0];
  const currentScreenshots = currentPlot
    ? screenshots[currentPlot.objectId]
    : [];
  const currentConvertedKrookieData = currentPlot
    ? convertedKrookieData.krookies[currentPlot.objectId] || null
    : null;
  const currentFormData = currentPlot
    ? formDataEditDetails[currentPlot.objectId] || null
    : null;

  const disabledPhaseField = Boolean(currentPlot.phaseId);

  useEffect(() => {
    if (!currentPlot || currentFormData) return;

    const { cityId, area, phaseId, number, landUseId, objectId } = currentPlot;

    if (currentConvertedKrookieData) {
      const initialFormData = {
        cityEn: {
          displayValue: currentConvertedKrookieData.cityEn.displayValue,
          value: currentConvertedKrookieData.cityEn.value,
        },
        phase: {
          displayValue: currentConvertedKrookieData.phase.displayValue || "",
          value: currentConvertedKrookieData.phase.value,
        },
        landUse: {
          displayValue: currentConvertedKrookieData.landUse.displayValue || "",
          value: currentConvertedKrookieData.landUse.value,
        },
        plotNumber: currentConvertedKrookieData.plotNumber,
        oldPlotNumber: currentConvertedKrookieData.oldPlotNumber,
        area: currentPlot.area,
        cityAr: currentConvertedKrookieData.cityAr,
        buildArea: currentConvertedKrookieData.buildArea,
        height: currentConvertedKrookieData.height,
        front: currentConvertedKrookieData.front,
        back: currentConvertedKrookieData.back,
        side: currentConvertedKrookieData.side,
        streetNumber: currentConvertedKrookieData.streetNumber,
        previousKrookieCode: currentConvertedKrookieData.previousKrookieCode,
        notes: currentConvertedKrookieData.notes,
      };
      dispatch(
        setFormDataEditDetails({
          id: objectId,
          formData: initialFormData,
        }),
      );
    } else {
      const selectedPlotsIds = selectedPlots.map((plot) => plot.objectId);
      const selectedPlotsNumbers = selectedPlots.map((plot) => plot.number);
      const initialFormData = {
        cityEn: { displayValue: cities.get(cityId) || "", value: cityId },
        phase: { displayValue: phase.get(phaseId) || "", value: phaseId },
        landUse: {
          displayValue: landUse.get(landUseId) || "",
          value: landUseId,
        },
        oldPlotNumber: number,
        previousKrookieCode: "",
        area,
        front: "7.5",
        back: "4.5",
        side: "3.5",
        plotNumber: selectedPlotsNumbers.join("-"),
      };

      dispatch(
        setFormDataEditDetails({
          id: objectId,
          formData: { ...initialFormData },
        }),
      );

      getPreviousKrookieNum(selectedPlotsIds).then((res) => {
        const previousCode = res ? res.join(", ") : "";

        dispatch(
          setFormDataEditDetails({
            id: objectId,
            formData: { previousKrookieCode: previousCode },
          }),
        );
      });
    }
  }, [
    currentPlot?.objectId,
    currentConvertedKrookieData,
    currentFormData,
    cities,
  ]);

  useEffect(() => {
    if (!currentPlot || !currentFormData) return;

    if (currentFormData && currentFormData.area !== currentPlot.area) {
      dispatch(
        setFormDataEditDetails({
          id: currentPlot.objectId,
          formData: { area: currentPlot.area },
        }),
      );
    }
  }, [currentPlot?.area, currentPlot?.objectId]);

  const requiredFields: (keyof FormDataEditDetails)[] = [
    "cityEn",
    "phase",
    "plotNumber",
    "streetNumber",
    "landUse",
    "buildArea",
    "area",
    "height",
    "front",
    "back",
    "side",
  ];

  const revision = `${formatDateWithoutTime(new Date())}_${getNewChangeNumber(currentConvertedKrookieData?.historyLog ?? [])}`;

  if (!currentFormData) {
    return null;
  }

  const cardKrookieData = {
    [currentPlot.objectId]: {
      ...currentFormData,
      serial: currentFormData.plotNumber,
      previousKrookieCode: currentFormData.previousKrookieCode,
      revision,
      wgs: currentPlot.spatialReference,
      drafterName: user?.userName,
      directorName: "",
      signatureDrafter: formatDateWithoutTime(new Date()),
      signatureDirector: "",
      tableData: ConvertToLines(currentPlot.newGeometry) ?? [],
      screenshots: {
        firstScreenshot: {
          scale: currentScreenshots[0]?.scale,
          url: currentScreenshots[0]?.url,
        },
        secondScreenshot: {
          scale: currentScreenshots[1]?.scale,
          url: currentScreenshots[1]?.url,
        },
      },
    },
  };

  const documentStatus =
    currentConvertedKrookieData?.statusId === KrookieStatus.Rejected
      ? KrookieStatus.Rejected
      : KrookieStatus.Project;

  return (
    <DetailsEditing
      requiredFields={requiredFields}
      cardKrookieData={cardKrookieData}
      krookieFormationTypeId={KrookieFormationTypeId.Merge}
      status={documentStatus}
      disabledFields={{
        cityEn: true,
        oldPlotNumber: true,
        phase: disabledPhaseField,
        area: true,
      }}
    />
  );
};

export default DetailsMerger;
